"use client";

import type { AudienceSurveyPosition } from "@/lib/analytics/nps-events";
import type { AudienceSurveyAnswers } from "@/lib/nps/submit";
import { cn } from "@/lib/utils";
import { XIcon } from "@phosphor-icons/react";
import Image from "next/image";
import { type ReactNode, type RefObject, useEffect, useRef, useState } from "react";

export type NPSInlineStep = "invite" | "audience" | "feedback" | "rate" | "thankyou";
interface NPSInlineProps {
  onStart: () => void;
  onSaveProgress: (
    details: AudienceSurveyAnswers & { lastStep: "audience" | "feedback" | "rate" }
  ) => void;
  onSubmitScore: (details: AudienceSurveyAnswers & { score: number }) => Promise<{ id: string }>;
  onComplete: (details: AudienceSurveyAnswers & { score: number }) => void;
  onDismiss: () => void;
  onStepChange?: (step: NPSInlineStep) => void;
  onPositionChange?: (position: AudienceSurveyPosition) => void;
  onStepCompleted?: (
    details:
      | { step: "audience"; answers: AudienceSurveyAnswers }
      | {
          step: "feedback";
          answers: AudienceSurveyAnswers;
          hasFeedback: boolean;
        }
  ) => void;
  className?: string;
}

const feedbackLimit = 300;
const feedbackCounterAt = 280;
type SurveyAudience = NonNullable<AudienceSurveyAnswers["audience"]>;
type SurveySetting = NonNullable<AudienceSurveyAnswers["setting"]>;
type SurveyRole = NonNullable<AudienceSurveyAnswers["role"]>;
type ChoiceOption<T extends string> = {
  value: T;
  label: string;
  icon: string;
};
const audienceOptions = [
  { value: "kid_youth", label: "Kid / Youth", icon: "victory-hand" },
  { value: "grown_up", label: "Grown-up", icon: "hot-beverage" },
] satisfies ChoiceOption<SurveyAudience>[];
const schoolRoleOptions = [
  { value: "teacher", label: "Teacher", icon: "red-apple" },
  { value: "admin", label: "Admin", icon: "briefcase" },
  { value: "specialist", label: "Specialist", icon: "books" },
  { value: "other", label: "Other", icon: "sparkles" },
] satisfies ChoiceOption<SurveyRole>[];
const homeRoleOptions = [
  { value: "parent_or_caregiver", label: "Parent or caregiver", icon: "busts" },
  { value: "homeschool_parent", label: "Homeschool parent", icon: "books" },
  { value: "just_playing", label: "Just playing", icon: "puzzle" },
] satisfies ChoiceOption<SurveyRole>[];
const kidPlaceOptions = [
  { value: "school", label: "School", icon: "school" },
  { value: "home", label: "Home", icon: "house" },
  { value: "somewhere_else", label: "Somewhere else", icon: "automobile" },
] satisfies ChoiceOption<SurveySetting>[];
const grownUpPlaceOptions = [
  { value: "school_or_organization", label: "School or organization", icon: "school" },
  { value: "at_home", label: "At home", icon: "house" },
] satisfies ChoiceOption<SurveySetting>[];
const progressSlots = ["who", "where", "role", "feedback", "rate"] as const;
const stageHeightClass = "h-[10.5rem]";
const iconCard =
  "min-w-0 overflow-hidden rounded-2xl border-2 px-1.5 py-3 text-center text-xs font-bold leading-tight sm:p-5 sm:text-sm";
const primary =
  "min-h-10 rounded-lg bg-brand-purple px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-purple-dark disabled:opacity-40";
const secondary = "min-h-10 rounded-lg px-3 py-2.5 text-sm text-gray-600 hover:bg-gray-100";
const inviteChoice =
  "inline-flex h-9 items-center justify-center gap-1.5 rounded-md border border-gray-200 px-3 text-sm font-semibold hover:border-brand-purple/50 hover:bg-brand-purple/5";
const titles: Record<NPSInlineStep, string> = {
  invite: "Help make PlayMath.org better!",
  audience: "I am a",
  feedback: "What would make PlayMath.org better?",
  rate: "One last question!",
  thankyou: "Thanks so much!",
};

type AudiencePhase = "who" | "where" | "role";

function audiencePhaseFor(
  audience: AudienceSurveyAnswers["audience"],
  setting: AudienceSurveyAnswers["setting"]
): AudiencePhase {
  if (!audience) return "who";
  if (audience === "grown_up" && setting) return "role";
  return "where";
}

function audienceHeading(
  audience: AudienceSurveyAnswers["audience"],
  phase: AudiencePhase
): string {
  if (phase === "who") return titles.audience;
  if (phase === "role") return "As a...";
  return audience === "kid_youth" ? "I'm at..." : "How do you use PlayMath.org?";
}

function surveyProgress(
  step: NPSInlineStep,
  phase: AudiencePhase
): { current: number; total: number } | null {
  if (step === "invite") return null;
  // Kids skip the "role" slot, so progress jumps from where (2) to feedback (4).
  const slot = step === "audience" ? phase : step === "thankyou" ? "rate" : step;
  return { current: progressSlots.indexOf(slot) + 1, total: progressSlots.length };
}

function ChoiceGrid<T extends string>({
  options,
  value,
  choose,
}: {
  options: readonly ChoiceOption<T>[];
  value: T | null;
  choose: (value: T) => void;
}) {
  return (
    <div
      className={cn(
        "grid gap-3",
        options.length === 4 ? "grid-cols-4" : options.length === 3 ? "grid-cols-3" : "grid-cols-2"
      )}
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          onClick={() => choose(option.value)}
          className={cn(
            iconCard,
            value === option.value
              ? "border-brand-purple bg-brand-purple/5 text-brand-purple"
              : "border-gray-200 hover:border-brand-purple/50"
          )}
        >
          <Image
            src={`/images/survey/${option.icon}.svg`}
            alt=""
            width={42}
            height={42}
            className="mx-auto mb-2 h-8 w-8 sm:mb-3 sm:h-[42px] sm:w-[42px]"
          />
          <span className="block text-balance">{option.label}</span>
        </button>
      ))}
    </div>
  );
}

function InviteRow({
  headingRef,
  onDismiss,
  onAccept,
}: {
  headingRef: RefObject<HTMLHeadingElement | null>;
  onDismiss: () => void;
  onAccept: () => void;
}) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
      <div className="min-w-0 sm:pr-2">
        <h2
          ref={headingRef}
          tabIndex={-1}
          className="font-charlie text-base font-normal leading-snug text-gray-900 outline-none sm:text-lg"
        >
          {titles.invite}
        </h2>
        <p className="text-xs text-gray-500 sm:text-sm">Got a minute for some quick questions?</p>
      </div>
      <div className="flex w-full items-center gap-2 sm:w-auto sm:shrink-0">
        <button
          type="button"
          onClick={onDismiss}
          className={cn(inviteChoice, "flex-1 sm:flex-none")}
        >
          <XIcon weight="bold" size={14} className="text-red-500" aria-hidden />
          No thanks!
        </button>
        <button
          type="button"
          onClick={onAccept}
          className={cn(inviteChoice, "flex-1 sm:flex-none")}
        >
          Sure!
        </button>
        <Image
          src="/images/survey/mochi.png"
          alt=""
          width={48}
          height={48}
          className="h-9 w-auto shrink-0"
          aria-hidden
        />
      </div>
    </div>
  );
}

function StageBody({ children }: { children: ReactNode }) {
  // Same height on every post-invite step, including rate and thank-you.
  return (
    <div data-testid="nps-stage" className={cn("flex flex-col overflow-hidden", stageHeightClass)}>
      {children}
    </div>
  );
}

function FeedbackField({
  feedback,
  onFeedback,
}: {
  feedback: string;
  onFeedback: (value: string) => void;
}) {
  return (
    <div className="relative min-h-0 flex-1">
      <textarea
        aria-label="Your ideas for PlayMath.org"
        value={feedback}
        onChange={(e) => onFeedback(e.target.value)}
        maxLength={feedbackLimit}
        rows={3}
        placeholder="I’d love a game about…"
        className="absolute inset-0 box-border h-full w-full resize-none rounded-2xl border-2 border-gray-200 p-3 pb-8 text-base focus:border-brand-purple focus:outline-none"
      />
      {feedback.length >= feedbackCounterAt && (
        <span className="pointer-events-none absolute bottom-3 right-3 text-xs text-gray-500">
          {feedback.length} / {feedbackLimit}
        </span>
      )}
    </div>
  );
}

function RateFields({
  score,
  busy,
  error,
  onScore,
}: {
  score: number | null;
  busy: boolean;
  error: string | null;
  onScore: (n: number) => void;
}) {
  return (
    <>
      <p className="mb-1.5 text-sm leading-snug text-gray-500">
        How likely are you to recommend PlayMath.org to a friend or colleague?
      </p>
      <fieldset disabled={busy}>
        <legend className="sr-only">Recommendation score from 0 to 10</legend>
        <div className="grid grid-cols-6 gap-2 sm:grid-cols-11 sm:gap-1">
          {Array.from({ length: 11 }, (_, n) => (
            <button
              key={n}
              type="button"
              aria-label={`Rate ${n} out of 10`}
              aria-pressed={score === n}
              onClick={() => onScore(n)}
              className={cn(
                "h-10 rounded-lg border-2 font-bold sm:h-12",
                score === n
                  ? "border-brand-purple bg-brand-purple text-white"
                  : "border-gray-200 hover:border-brand-purple/50"
              )}
            >
              {n}
            </button>
          ))}
        </div>
      </fieldset>
      <div className="mt-1.5 flex justify-between gap-5 text-xs text-gray-500">
        <span>0 · Not at all likely</span>
        <span>10 · Extremely likely</span>
      </div>
      {error && (
        <p role="alert" className="mt-4 text-sm text-red-600">
          {error}
        </p>
      )}
    </>
  );
}

function QuestionChrome({
  headingRef,
  title,
  onBack,
  backDisabled,
  trailing,
}: {
  headingRef: RefObject<HTMLHeadingElement | null>;
  title: string;
  onBack?: () => void;
  backDisabled?: boolean;
  trailing?: ReactNode;
}) {
  return (
    <div className="mb-2 grid grid-cols-[6.5rem_minmax(0,1fr)_6.5rem] grid-rows-[2.5rem_2.5rem] items-center gap-x-2 gap-y-2 sm:mb-3 sm:grid-rows-[2.5rem] sm:gap-y-0">
      <div className="col-start-1 row-start-1 flex h-10 items-center justify-start">
        {onBack ? (
          <button type="button" className={secondary} disabled={backDisabled} onClick={onBack}>
            ← Back
          </button>
        ) : null}
      </div>
      <h2
        ref={headingRef}
        tabIndex={-1}
        className="col-span-3 row-start-2 text-center font-charlie text-base font-normal leading-snug text-balance outline-none sm:col-span-1 sm:col-start-2 sm:row-start-1 sm:truncate sm:text-xl sm:leading-none"
      >
        {title}
      </h2>
      <div className="col-start-3 row-start-1 flex h-10 items-center justify-end">{trailing}</div>
    </div>
  );
}

function QuestionTrailing({
  step,
  score,
  busy,
  onNextFeedback,
  onSubmit,
}: {
  step: NPSInlineStep;
  score: number | null;
  busy: boolean;
  onNextFeedback: () => void;
  onSubmit: () => void;
}) {
  if (step === "feedback") {
    return (
      <button type="button" className={primary} onClick={onNextFeedback}>
        Next →
      </button>
    );
  }
  if (step === "rate") {
    return (
      <button
        type="button"
        className={primary}
        disabled={score === null || busy}
        onClick={onSubmit}
      >
        {busy ? "Submitting..." : "Submit"}
      </button>
    );
  }
  return null;
}

function AudienceFields({
  phase,
  audience,
  setting,
  role,
  onWho,
  onWhere,
  onRole,
}: {
  phase: AudiencePhase;
  audience: AudienceSurveyAnswers["audience"];
  setting: AudienceSurveyAnswers["setting"];
  role: AudienceSurveyAnswers["role"];
  onWho: (value: SurveyAudience) => void;
  onWhere: (value: SurveySetting) => void;
  onRole: (value: SurveyRole) => void;
}) {
  if (phase === "who") {
    return <ChoiceGrid options={audienceOptions} value={audience} choose={onWho} />;
  }
  if (phase === "where") {
    return (
      <ChoiceGrid
        options={audience === "kid_youth" ? kidPlaceOptions : grownUpPlaceOptions}
        value={setting}
        choose={onWhere}
      />
    );
  }
  return (
    <ChoiceGrid
      options={setting === "at_home" ? homeRoleOptions : schoolRoleOptions}
      value={role}
      choose={onRole}
    />
  );
}

function ThankYouFields({ onComplete }: { onComplete: () => void }) {
  return (
    <div className="flex h-full flex-col items-center justify-center">
      <Image
        src="/images/survey/mochi.png"
        alt=""
        width={48}
        height={48}
        className="mb-1 h-10 w-auto"
        aria-hidden
      />
      <p className="mb-2 text-center text-sm text-gray-500">
        We appreciate your time and will do our best!
      </p>
      <button
        type="button"
        className={cn(primary, "w-full sm:mx-auto sm:block sm:w-auto")}
        onClick={onComplete}
      >
        Back to playing →
      </button>
    </div>
  );
}

function SurveyFields({
  step,
  audiencePhase,
  audience,
  setting,
  role,
  feedback,
  score,
  busy,
  error,
  onWho,
  onWhere,
  onRole,
  onFeedback,
  onScore,
  onComplete,
}: {
  step: NPSInlineStep;
  audiencePhase: AudiencePhase;
  audience: AudienceSurveyAnswers["audience"];
  setting: AudienceSurveyAnswers["setting"];
  role: AudienceSurveyAnswers["role"];
  feedback: string;
  score: number | null;
  busy: boolean;
  error: string | null;
  onWho: (value: SurveyAudience) => void;
  onWhere: (value: SurveySetting) => void;
  onRole: (value: SurveyRole) => void;
  onFeedback: (value: string) => void;
  onScore: (n: number) => void;
  onComplete: () => void;
}) {
  const body =
    step === "audience" ? (
      <AudienceFields
        phase={audiencePhase}
        audience={audience}
        setting={setting}
        role={role}
        onWho={onWho}
        onWhere={onWhere}
        onRole={onRole}
      />
    ) : step === "feedback" ? (
      <FeedbackField feedback={feedback} onFeedback={onFeedback} />
    ) : step === "rate" ? (
      <RateFields score={score} busy={busy} error={error} onScore={onScore} />
    ) : step === "thankyou" ? (
      <ThankYouFields onComplete={onComplete} />
    ) : null;
  if (!body) return null;
  return <StageBody>{body}</StageBody>;
}

export function NPSInline({
  onStart,
  onSaveProgress,
  onSubmitScore,
  onComplete,
  onDismiss,
  onStepChange,
  onPositionChange,
  onStepCompleted,
  className,
}: NPSInlineProps) {
  const [step, setStep] = useState<NPSInlineStep>("invite");
  const [audience, setAudience] = useState<AudienceSurveyAnswers["audience"]>(null);
  const [setting, setSetting] = useState<AudienceSurveyAnswers["setting"]>(null);
  const [role, setRole] = useState<AudienceSurveyAnswers["role"]>(null);
  const [feedback, setFeedback] = useState("");
  const [score, setScore] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submitting = useRef(false);
  const heading = useRef<HTMLHeadingElement>(null);
  const audiencePhase = audiencePhaseFor(audience, setting);
  const progress = surveyProgress(step, audiencePhase);
  const headingText = step === "audience" ? audienceHeading(audience, audiencePhase) : titles[step];
  const analyticsPosition: AudienceSurveyPosition =
    step === "audience" ? `audience_${audiencePhase}` : step === "rate" ? "score" : step;
  const surveyAnswers = (next: Partial<AudienceSurveyAnswers> = {}): AudienceSurveyAnswers => {
    return {
      audience: next.audience === undefined ? audience : next.audience,
      setting: next.setting === undefined ? setting : next.setting,
      role: next.role === undefined ? role : next.role,
      feedback: next.feedback ?? feedback.trim(),
      score: next.score === undefined ? score : next.score,
    };
  };
  const saveProgress = (
    lastStep: "audience" | "feedback" | "rate",
    next?: Parameters<typeof surveyAnswers>[0]
  ) => {
    onSaveProgress({ ...surveyAnswers(next), lastStep });
  };
  useEffect(() => {
    onStepChange?.(step);
    onPositionChange?.(analyticsPosition);
    heading.current?.focus();
  }, [step, audiencePhase, analyticsPosition, onStepChange, onPositionChange]);
  const goBack = () => {
    if (step === "audience" && audiencePhase === "role") {
      setSetting(null);
      setRole(null);
      saveProgress("audience", { setting: null, role: null });
      return;
    }
    if (step === "audience" && audiencePhase === "where") {
      setAudience(null);
      setSetting(null);
      setRole(null);
      saveProgress("audience", { audience: null, setting: null, role: null });
      return;
    }
    if (step === "feedback") {
      setStep("audience");
      saveProgress("audience");
      return;
    }
    if (step === "rate") {
      setError(null);
      setStep("feedback");
      saveProgress("feedback");
    }
  };
  const submit = async () => {
    if (score === null || submitting.current) return;
    submitting.current = true;
    setBusy(true);
    setError(null);
    try {
      await onSubmitScore({ ...surveyAnswers(), score });
      setStep("thankyou");
    } catch {
      setError("Couldn't save your response. Please try again.");
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };
  return (
    <div
      className={cn(
        "font-ubuntu text-gray-900 [&_button:focus-visible]:outline [&_button:focus-visible]:outline-2 [&_button:focus-visible]:outline-offset-4 [&_button:focus-visible]:outline-brand-purple",
        className
      )}
      data-testid="nps-inline"
      aria-busy={busy}
    >
      {progress && (
        <div
          className="mb-3 flex items-center gap-2"
          aria-label={`Step ${progress.current} of ${progress.total}`}
        >
          {Array.from({ length: progress.total }, (_, i) => i + 1).map((n) => (
            <span
              key={n}
              className={cn(
                "h-1 flex-1 rounded-full",
                n <= progress.current ? "bg-brand-purple" : "bg-gray-200"
              )}
            />
          ))}
        </div>
      )}
      {step === "invite" ? (
        <InviteRow
          headingRef={heading}
          onDismiss={onDismiss}
          onAccept={() => {
            onStart();
            setStep("audience");
          }}
        />
      ) : (
        <QuestionChrome
          headingRef={heading}
          title={headingText}
          onBack={
            step === "thankyou" || (step === "audience" && audiencePhase === "who")
              ? undefined
              : goBack
          }
          backDisabled={busy}
          trailing={
            step === "thankyou" ? null : (
              <QuestionTrailing
                step={step}
                score={score}
                busy={busy}
                onNextFeedback={() => {
                  const answers = surveyAnswers();
                  onStepCompleted?.({
                    step: "feedback",
                    answers,
                    hasFeedback: Boolean(answers.feedback),
                  });
                  saveProgress("rate");
                  setStep("rate");
                }}
                onSubmit={() => {
                  void submit();
                }}
              />
            )
          }
        />
      )}
      <SurveyFields
        step={step}
        audiencePhase={audiencePhase}
        audience={audience}
        setting={setting}
        role={role}
        feedback={feedback}
        score={score}
        busy={busy}
        error={error}
        onWho={(value) => {
          setAudience(value);
          setSetting(null);
          setRole(null);
          saveProgress("audience", { audience: value, setting: null, role: null });
        }}
        onWhere={(value) => {
          setSetting(value);
          setRole(null);
          if (audience === "kid_youth") {
            onStepCompleted?.({
              step: "audience",
              answers: surveyAnswers({ setting: value, role: null }),
            });
            saveProgress("feedback", { setting: value, role: null });
            setStep("feedback");
          } else {
            saveProgress("audience", { setting: value, role: null });
          }
        }}
        onRole={(value) => {
          setRole(value);
          onStepCompleted?.({
            step: "audience",
            answers: surveyAnswers({ role: value }),
          });
          saveProgress("feedback", { role: value });
          setStep("feedback");
        }}
        onFeedback={setFeedback}
        onScore={(nextScore) => {
          setScore(nextScore);
          saveProgress("rate", { score: nextScore });
        }}
        onComplete={() => onComplete({ ...surveyAnswers(), score: score! })}
      />
    </div>
  );
}
